import api from './api';

class MoondreamService {
  /**
   * Analyze an image with a custom prompt
   * @param {string} imagePath - Path to the image file
   * @param {string} prompt - The analysis prompt
   * @returns {Promise<Object>}
   */
  async analyzeImage(imagePath, prompt) {
    try {
      const response = await api.post('/moondream/analyze', {
        image_path: imagePath,
        prompt: prompt
      });
      return response.data;
    } catch (error) {
      console.error('Error analyzing image:', error);
      throw error;
    }
  }

  /**
   * Extract text from property sign images
   * @param {string} imagePath - Path to the image file
   * @returns {Promise<Object>}
   */
  async extractPropertySignText(imagePath) {
    try {
      const response = await api.post('/moondream/extract-property-sign-text', {
        image_path: imagePath
      });
      return response.data;
    } catch (error) {
      console.error('Error extracting property sign text:', error);
      throw error;
    }
  }

  /**
   * Analyze property photos for features and condition
   * @param {string} imagePath - Path to the image file
   * @returns {Promise<Object>}
   */
  async analyzePropertyPhoto(imagePath) {
    try {
      const response = await api.post('/moondream/analyze-property-photo', {
        image_path: imagePath
      });
      return response.data;
    } catch (error) {
      console.error('Error analyzing property photo:', error);
      throw error;
    }
  }

  /**
   * Detect objects and features in property images
   * @param {string} imagePath - Path to the image file
   * @returns {Promise<Object>}
   */
  async detectPropertyFeatures(imagePath) {
    try {
      const response = await api.post('/moondream/detect-property-features', {
        image_path: imagePath
      });
      return response.data;
    } catch (error) {
      console.error('Error detecting property features:', error);
      throw error;
    }
  }

  /**
   * Validate if property sign is visible and readable
   * @param {string} imagePath - Path to the image file
   * @returns {Promise<Object>}
   */
  async validatePropertySign(imagePath) {
    try {
      const response = await api.post('/moondream/validate-property-sign', {
        image_path: imagePath
      });
      return response.data;
    } catch (error) {
      console.error('Error validating property sign:', error);
      throw error;
    }
  }

  /**
   * Analyze multiple images in batch
   * @param {string[]} imagePaths - Array of image paths
   * @param {string} prompt - The analysis prompt
   * @returns {Promise<Object>}
   */
  async analyzeBatch(imagePaths, prompt) {
    try {
      const response = await api.post('/moondream/analyze-batch', {
        image_paths: imagePaths,
        prompt: prompt
      });
      return response.data;
    } catch (error) {
      console.error('Error analyzing batch:', error);
      throw error;
    }
  }

  /**
   * Upload and analyze an image file
   * @param {File} imageFile - The image file to upload
   * @param {string} prompt - The analysis prompt
   * @returns {Promise<Object>}
   */
  async uploadAndAnalyze(imageFile, prompt) {
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      formData.append('prompt', prompt);

      const response = await api.post('/moondream/upload-and-analyze', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading and analyzing image:', error);
      throw error;
    }
  }

  /**
   * Get API status and quota information
   * @returns {Promise<Object>}
   */
  async getApiStatus() {
    try {
      const response = await api.get('/moondream/status');
      return response.data;
    } catch (error) {
      console.error('Error getting API status:', error);
      throw error;
    }
  }

  /**
   * Analyze property submission images (sale sign + property photos)
   * @param {string} saleSignPath - Path to the sale sign image
   * @param {string[]} propertyPhotoPaths - Array of property photo paths
   * @returns {Promise<Object>}
   */
  async analyzePropertySubmission(saleSignPath, propertyPhotoPaths) {
    try {
      const results = {
        saleSign: null,
        propertyPhotos: [],
        summary: null
      };

      // Analyze sale sign
      if (saleSignPath) {
        console.log('Analyzing sale sign...');
        results.saleSign = await this.extractPropertySignText(saleSignPath);
      }

      // Analyze property photos
      if (propertyPhotoPaths && propertyPhotoPaths.length > 0) {
        console.log(`Analyzing ${propertyPhotoPaths.length} property photos...`);
        for (let i = 0; i < propertyPhotoPaths.length; i++) {
          const photoPath = propertyPhotoPaths[i];
          console.log(`Analyzing photo ${i + 1}/${propertyPhotoPaths.length}...`);
          
          const photoAnalysis = await this.analyzePropertyPhoto(photoPath);
          results.propertyPhotos.push({
            path: photoPath,
            analysis: photoAnalysis
          });

          // Add small delay to avoid rate limiting
          if (i < propertyPhotoPaths.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
      }

      // Generate summary
      results.summary = this.generatePropertySummary(results);

      return results;
    } catch (error) {
      console.error('Error analyzing property submission:', error);
      throw error;
    }
  }

  /**
   * Generate a summary from analysis results
   * @param {Object} results - Analysis results
   * @returns {Object}
   */
  generatePropertySummary(results) {
    const summary = {
      hasSaleSign: !!results.saleSign,
      saleSignText: null,
      propertyType: null,
      condition: null,
      features: [],
      issues: [],
      estimatedValue: null
    };

    // Extract sale sign text
    if (results.saleSign && results.saleSign.success) {
      summary.saleSignText = results.saleSign.data;
    }

    // Analyze property photos
    if (results.propertyPhotos && results.propertyPhotos.length > 0) {
      const allFeatures = [];
      const allIssues = [];

      results.propertyPhotos.forEach(photo => {
        if (photo.analysis && photo.analysis.success) {
          const analysis = photo.analysis.data;
          // Extract features and issues from analysis
          // This would need to be customized based on Moondream's response format
          if (analysis.features) {
            allFeatures.push(...analysis.features);
          }
          if (analysis.issues) {
            allIssues.push(...analysis.issues);
          }
        }
      });

      summary.features = [...new Set(allFeatures)]; // Remove duplicates
      summary.issues = [...new Set(allIssues)]; // Remove duplicates
    }

    return summary;
  }

  /**
   * Convert image file to base64 for direct API calls
   * @param {File} file - Image file
   * @returns {Promise<string>}
   */
  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  }

  /**
   * Validate image file before analysis
   * @param {File} file - Image file
   * @returns {Object}
   */
  validateImageFile(file) {
    const errors = [];

    if (!file) {
      errors.push('No file provided');
      return { isValid: false, errors };
    }

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      errors.push('Invalid file type. Only JPEG, PNG, and WebP images are allowed.');
    }

    // Check file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      errors.push('File size too large. Maximum size is 10MB.');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

const moondreamService = new MoondreamService();
export default moondreamService;
